import { useEffect, useMemo, useState } from 'react';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { QuickReplyChips } from './QuickReplyChips';
import { ChatInputBar } from './ChatInputBar';
import { assistantConfig, challans, selectedVehicle, type Intent } from '../data/challans';
import { getAssistantResponse } from '../utils/getAssistantResponse';
import { matchIntent } from '../utils/intentMatcher';
import styles from './ChatSheet.module.css';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

interface ChatSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPING_DELAY_MS = 800;

export function ChatSheet({ isOpen, onClose }: ChatSheetProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  const welcomeText = useMemo(() => {
    if (!selectedVehicle || challans.length === 0) {
      return 'Select a vehicle to ask about your challans.';
    }
    return `Hi! I'm ${assistantConfig.name}. Ask me anything about your challans for **${selectedVehicle}**.`;
  }, []);

  const addUserMessage = (text: string) => {
    const userMessage: Message = {
      id: `${Date.now()}-user`,
      role: 'user',
      text,
    };
    setMessages((prev) => [...prev, userMessage]);
  };

  const addAssistantReply = (intent: Intent) => {
    setIsTyping(true);
    const replyText = getAssistantResponse(intent, selectedVehicle, challans);

    window.setTimeout(() => {
      const assistantMessage: Message = {
        id: `${Date.now()}-assistant`,
        role: 'assistant',
        text: replyText,
      };
      setIsTyping(false);
      setMessages((prev) => [...prev, assistantMessage]);
    }, TYPING_DELAY_MS);
  };

  const handleQuickReply = (intent: Intent, label: string) => {
    if (isTyping) return;
    addUserMessage(label);
    addAssistantReply(intent);
  };

  const handleTypedMessage = (text: string) => {
    if (isTyping || !text.trim()) return;
    const intent = matchIntent(text);
    addUserMessage(text);
    addAssistantReply(intent);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={styles.sheet}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${assistantConfig.name} chat`}
      >
        <ChatHeader onClose={onClose} />
        <MessageList messages={messages} isTyping={isTyping} welcomeText={welcomeText} />
        <QuickReplyChips onSelect={handleQuickReply} />
        <ChatInputBar onSend={handleTypedMessage} disabled={isTyping} />
      </div>
    </div>
  );
}
